import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
  Alert,
  AlertTitle,
  Fade,
  Link,
} from '@mui/material'
import { Upload as UploadIcon, Delete as DeleteIcon } from '@mui/icons-material'

// Define the API URL
const API_URL = process.env.REACT_APP_ADMIN_API

// Define the questionnaire field type
interface QuestionnaireField {
  id?: string
  label: string
  source: string
  required?: boolean
  value?: string
  fieldType?: 'yesno' | 'dropdown' | 'attachment'
  options?: Array<{
    text: string
    score?: number
  }>
  attachments?: Array<{
    id: string
    filename: string
    url?: string
  }>
  allow_vendor_comment?: boolean
}

// Define the questionnaire section type
interface QuestionnaireSection {
  id?: string
  title: string
  fields: QuestionnaireField[]
}

// Define the questionnaire data type
interface QuestionnaireData {
  status: string
  message?: string
  questionnaire_id?: number
  vendor_id?: number
  organisation_id?: number
  questionnaire_data?: QuestionnaireSection[]
  allow_additional_document_uploads?: boolean
  is_clarification?: boolean
  completed_questionnaire?: Record<string, string>
  clarification_requests?: Record<string, { question: string; comment: string }>
}

const PublicQuestionnaire = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [questionnaireData, setQuestionnaireData] =
    useState<QuestionnaireData | null>(null)
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [clarificationResponses, setClarificationResponses] = useState<
    Record<string, string>
  >({})
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [submitted, setSubmitted] = useState<boolean>(false)
  const [linkRegenerated, setLinkRegenerated] = useState<boolean>(false)
  const [clarificationFields, setClarificationFields] = useState<Set<string>>(
    new Set()
  )
  const [additionalAttachments, setAdditionalAttachments] = useState<
    Array<{
      id: string
      filename: string
      url?: string
    }>
  >([])
  const additionalAttachmentsFieldId = 'additional_document_uploads'

  // Fetch the questionnaire data
  useEffect(() => {
    const fetchQuestionnaireData = async () => {
      try {
        setLoading(true)
        const response = await fetch(`${API_URL}/questionnaire/${token}`)

        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`)
        }

        const data = await response.json()
        setQuestionnaireData(data)

        // Initialize answers with empty values or pre-filled values for clarification
        if (data.status === 'valid' && data.questionnaire_data) {
          let initialAnswers: Record<string, string> = {}

          // If this is a clarification request, use the original answers and track clarification fields
          if (data.is_clarification && data.completed_questionnaire) {
            initialAnswers = { ...data.completed_questionnaire }
            console.log(
              'Clarification request detected, pre-filling with original answers:',
              initialAnswers
            )

            // Track which fields need clarification
            if (data.clarification_requests) {
              const fieldsNeedingClarification = new Set(
                Object.keys(data.clarification_requests)
              )
              setClarificationFields(fieldsNeedingClarification)
              console.log(
                'Fields needing clarification:',
                fieldsNeedingClarification
              )

              // Initialize clarification responses with empty values
              const initialClarificationResponses: Record<string, string> = {}
              fieldsNeedingClarification.forEach((field) => {
                initialClarificationResponses[field] = ''
              })
              setClarificationResponses(initialClarificationResponses)
            }
          } else {
            // Otherwise, initialize with empty values
            data.questionnaire_data.forEach((section) => {
              section.fields.forEach((field) => {
                initialAnswers[field.source] = ''
              })
            })
          }

          setAnswers(initialAnswers)
        }
      } catch (err) {
        console.error('Error fetching questionnaire:', err)
        setError(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
      } finally {
        setLoading(false)
      }
    }

    if (token) {
      fetchQuestionnaireData()
    }
  }, [token])

  // Handle input change
  const handleInputChange = (source: string, value: string) => {
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [source]: value,
    }))
  }

  // Handle clarification response change
  const handleClarificationResponseChange = (source: string, value: string) => {
    setClarificationResponses((prevResponses) => ({
      ...prevResponses,
      [source]: value,
    }))

    // Also update the answers state for backward compatibility
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [source]: value,
    }))
  }

  // Check if all required fields within the current section except the current attachment field are filled
  const areRequiredFieldsFilled = (currentFieldId: string): boolean => {
    if (!questionnaireData?.questionnaire_data) return false

    // Find the section that contains the current field
    const currentSection = questionnaireData.questionnaire_data.find(
      (section) =>
        section.fields.some((field) => field.source === currentFieldId)
    )

    if (!currentSection) return false

    // Check each required field in the current section
    for (const field of currentSection.fields) {
      // Skip the current attachment field
      if (field.source === currentFieldId) continue

      // Check if this is a required field that's not filled
      if (
        field.required &&
        (!answers[field.source] || answers[field.source] === '')
      ) {
        return false
      }
    }

    return true
  }

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    // Validate required fields
    const requiredFields: string[] = []
    questionnaireData?.questionnaire_data?.forEach((section) => {
      section.fields.forEach((field) => {
        // For clarification requests, only validate fields that need clarification
        const needsValidation =
          !questionnaireData.is_clarification ||
          clarificationFields.has(field.source)
        if (field.required && needsValidation && !answers[field.source]) {
          requiredFields.push(field.label)
        }
      })
    })

    if (requiredFields.length > 0) {
      setError(
        `Please fill in the following required fields: ${requiredFields.join(
          ', '
        )}`
      )
      return
    }

    try {
      setSubmitting(true)

      // If this is a clarification request, use the clarification endpoint
      const endpoint = questionnaireData?.is_clarification
        ? `${API_URL}/questionnaire/${token}/submit-clarification`
        : `${API_URL}/questionnaire/${token}/submit`

      // For clarification requests, only send the fields that need clarification
      const submissionData = questionnaireData?.is_clarification
        ? clarificationResponses
        : answers

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      })

      if (!response.ok) {
        // Try to parse the error response to get detailed error message
        const errorData = await response.json().catch(() => null)

        // Check if this is an expired link error (status 400 with specific message)
        if (
          response.status === 400 &&
          errorData?.detail === 'This link has expired'
        ) {
          // Update the questionnaire data to show the expired link UI
          setQuestionnaireData((prevData) => ({
            ...prevData,
            status: 'expired',
            message:
              'This questionnaire link has expired. Please request a new link.',
          }))
          return
        }

        throw new Error(
          errorData?.detail ||
            `Error ${response.status}: ${response.statusText}`
        )
      }

      const data = await response.json()
      setSubmitted(true)
      setError(null)
    } catch (err) {
      console.error('Error submitting questionnaire:', err)
      setError(err instanceof Error ? err.message : 'An unknown error occurred')
    } finally {
      setSubmitting(false)
    }
  }

  // Handle regenerating an expired link
  const handleRegenerateLink = async () => {
    try {
      setLoading(true)
      const response = await fetch(
        `${API_URL}/questionnaire/${token}/regenerate`,
        {
          method: 'POST',
        }
      )

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()
      setError(null)
      setLinkRegenerated(true)
    } catch (err) {
      console.error('Error regenerating link:', err)
      setError(err instanceof Error ? err.message : 'An unknown error occurred')
    } finally {
      setLoading(false)
    }
  }

  // Render loading state
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    )
  }

  // Render error state
  if (error && !questionnaireData) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        p={3}
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 600, width: '100%' }}>
          <Typography variant="h5" color="error" gutterBottom>
            Error
          </Typography>
          <Typography paragraph>{error}</Typography>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </Paper>
      </Box>
    )
  }

  // Render expired link state
  if (questionnaireData?.status === 'expired') {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        p={3}
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 600, width: '100%' }}>
          <Typography variant="h5" gutterBottom>
            Link Expired
          </Typography>

          {linkRegenerated ? (
            <Fade in={linkRegenerated}>
              <Alert severity="success" sx={{ mb: 3 }}>
                <AlertTitle>Success</AlertTitle>A new link has been sent to the
                vendor email address.
              </Alert>
            </Fade>
          ) : (
            <Typography paragraph>
              This questionnaire link has expired. Please click the button below
              to request a new link.
            </Typography>
          )}

          <Button
            variant="contained"
            onClick={handleRegenerateLink}
            disabled={loading || linkRegenerated}
            sx={{ mt: linkRegenerated ? 2 : 0 }}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : linkRegenerated ? (
              'Link Sent'
            ) : (
              'Request New Link'
            )}
          </Button>
        </Paper>
      </Box>
    )
  }

  // Render already submitted state
  if (questionnaireData?.status === 'completed') {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        p={3}
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 600, width: '100%' }}>
          <Typography variant="h5" gutterBottom>
            Questionnaire Already Submitted
          </Typography>
          <Typography paragraph>
            {questionnaireData.message ||
              'This questionnaire has already been submitted. Thank you for your participation.'}
          </Typography>
        </Paper>
      </Box>
    )
  }

  // Render submitted state
  if (submitted) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        p={3}
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 600, width: '100%' }}>
          <Typography variant="h5" gutterBottom>
            Questionnaire Submitted
          </Typography>
          <Typography paragraph>
            Thank you for completing the questionnaire. Your responses have been
            submitted successfully.
          </Typography>
        </Paper>
      </Box>
    )
  }

  // Render the questionnaire form
  return (
    <Box p={3} maxWidth={800} mx="auto" my={4}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          {questionnaireData?.is_clarification
            ? 'Questionnaire - Clarification Requested'
            : 'Questionnaire'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <AlertTitle>Error</AlertTitle>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          {questionnaireData?.questionnaire_data?.map(
            (section, sectionIndex) => (
              <Box key={section.id || sectionIndex} mb={4}>
                <Typography variant="h5" gutterBottom>
                  {section.title}
                </Typography>

                {section.fields.map((field, fieldIndex) => (
                  <Box key={field.id || fieldIndex} mb={3}>
                    {field.fieldType === 'yesno' ? (
                      <Box>
                        <FormControl
                          fullWidth
                          required={field.required}
                          error={
                            field.required &&
                            !answers[field.source] &&
                            error !== null
                          }
                          sx={{
                            backgroundColor:
                              questionnaireData?.is_clarification &&
                              clarificationFields.has(field.source)
                                ? 'rgba(255, 244, 229, 0.2)' // Light warning color background
                                : 'inherit',
                          }}
                        >
                          <Typography variant="subtitle1" gutterBottom>
                            {field.label}
                            {field.required ? ' *' : ''}
                          </Typography>
                          <RadioGroup
                            name={field.source}
                            value={answers[field.source] || ''}
                            onChange={(e) =>
                              handleInputChange(field.source, e.target.value)
                            }
                          >
                            <FormControlLabel
                              value="yes"
                              control={<Radio />}
                              label="Yes"
                              disabled={questionnaireData?.is_clarification}
                            />
                            <FormControlLabel
                              value="no"
                              control={<Radio />}
                              label="No"
                              disabled={questionnaireData?.is_clarification}
                            />
                          </RadioGroup>
                          {field.required &&
                            !answers[field.source] &&
                            error !== null && (
                              <FormHelperText>
                                This field is required
                              </FormHelperText>
                            )}
                          {questionnaireData?.is_clarification &&
                            !clarificationFields.has(field.source) && (
                              <FormHelperText>
                                This field does not need clarification
                              </FormHelperText>
                            )}
                        </FormControl>

                        {/* Show clarification request if this field needs clarification */}
                        {questionnaireData?.is_clarification &&
                          clarificationFields.has(field.source) && (
                            <>
                              <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                                <AlertTitle>Clarification Requested</AlertTitle>
                                <Typography variant="body2" gutterBottom>
                                  {questionnaireData.clarification_requests?.[
                                    field.source
                                  ]?.comment ||
                                    'Please provide more information for this question.'}
                                </Typography>
                              </Alert>
                              <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Your Clarification Response"
                                name={`${field.source}_clarification`}
                                value={
                                  clarificationResponses[field.source] || ''
                                }
                                onChange={(e) =>
                                  handleClarificationResponseChange(
                                    field.source,
                                    e.target.value
                                  )
                                }
                                variant="outlined"
                                sx={{ mt: 1 }}
                                placeholder="Please provide additional information as requested..."
                              />
                            </>
                          )}
                      </Box>
                    ) : field.fieldType === 'dropdown' && field.options ? (
                      <Box>
                        <FormControl
                          fullWidth
                          required={field.required}
                          error={
                            field.required &&
                            !answers[field.source] &&
                            error !== null
                          }
                          sx={{
                            backgroundColor:
                              questionnaireData?.is_clarification &&
                              clarificationFields.has(field.source)
                                ? 'rgba(255, 244, 229, 0.2)' // Light warning color background
                                : 'inherit',
                          }}
                        >
                          <InputLabel>{field.label}</InputLabel>
                          <Select
                            value={answers[field.source] || ''}
                            onChange={(e) =>
                              handleInputChange(field.source, e.target.value)
                            }
                            label={field.label}
                            disabled={questionnaireData?.is_clarification}
                          >
                            <MenuItem value="">
                              <em>Select an option</em>
                            </MenuItem>
                            {field.options.map((option, optionIndex) => (
                              <MenuItem key={optionIndex} value={option.text}>
                                {option.text}
                              </MenuItem>
                            ))}
                          </Select>
                          {field.required &&
                            !answers[field.source] &&
                            error !== null && (
                              <FormHelperText>
                                This field is required
                              </FormHelperText>
                            )}
                          {questionnaireData?.is_clarification &&
                            !clarificationFields.has(field.source) && (
                              <FormHelperText>
                                This field does not need clarification
                              </FormHelperText>
                            )}
                        </FormControl>

                        {/* Show clarification request if this field needs clarification */}
                        {questionnaireData?.is_clarification &&
                          clarificationFields.has(field.source) && (
                            <>
                              <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                                <AlertTitle>Clarification Requested</AlertTitle>
                                <Typography variant="body2" gutterBottom>
                                  {questionnaireData.clarification_requests?.[
                                    field.source
                                  ]?.comment ||
                                    'Please provide more information for this question.'}
                                </Typography>
                              </Alert>
                              <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Your Clarification Response"
                                name={`${field.source}_clarification`}
                                value={
                                  clarificationResponses[field.source] || ''
                                }
                                onChange={(e) =>
                                  handleClarificationResponseChange(
                                    field.source,
                                    e.target.value
                                  )
                                }
                                variant="outlined"
                                sx={{ mt: 1 }}
                                placeholder="Please provide additional information as requested..."
                              />
                            </>
                          )}
                      </Box>
                    ) : field.fieldType === 'attachment' ? (
                      <Box
                        sx={{
                          backgroundColor:
                            questionnaireData?.is_clarification &&
                            clarificationFields.has(field.source)
                              ? 'rgba(255, 244, 229, 0.2)' // Light warning color background
                              : 'inherit',
                          padding: 2,
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="subtitle1" gutterBottom>
                          {field.label}
                          {field.required ? ' *' : ''}
                        </Typography>

                        {/* Display original uploaded files */}
                        {questionnaireData?.is_clarification && (
                          <Box mb={2}>
                            <Typography variant="subtitle2" gutterBottom>
                              Previously Uploaded:
                            </Typography>
                            {field.attachments &&
                            field.attachments.length > 0 ? (
                              <Box>
                                {field.attachments.map((attachment, index) => (
                                  <Box
                                    key={attachment.id || index}
                                    sx={{ mb: 1 }}
                                  >
                                    <Link
                                      href={attachment.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {attachment.filename}
                                    </Link>
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                {questionnaireData?.is_clarification &&
                                !clarificationFields.has(field.source)
                                  ? 'No filename available for previously uploaded file'
                                  : 'No files were previously uploaded'}
                              </Typography>
                            )}
                          </Box>
                        )}

                        {/* Show "File previously uploaded" message for fields that don't need clarification */}
                        {questionnaireData?.is_clarification &&
                          !clarificationFields.has(field.source) && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                              <Typography variant="body2">
                                {field.attachments &&
                                field.attachments.length > 0 ? (
                                  <>
                                    File previously uploaded:{' '}
                                    {field.attachments.map(
                                      (attachment, index) => (
                                        <Link
                                          key={attachment.id || index}
                                          href={attachment.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          sx={{ ml: 1 }}
                                        >
                                          {attachment.filename}
                                        </Link>
                                      )
                                    )}
                                    . This field does not need clarification.
                                  </>
                                ) : (
                                  'File previously uploaded. This field does not need clarification.'
                                )}
                              </Typography>
                            </Alert>
                          )}

                        {/* Show clarification request if this field needs clarification */}
                        {questionnaireData?.is_clarification &&
                          clarificationFields.has(field.source) && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                              <AlertTitle>Clarification Requested</AlertTitle>
                              <Typography variant="body2" gutterBottom>
                                {questionnaireData.clarification_requests?.[
                                  field.source
                                ]?.comment ||
                                  'Please provide more information for this question.'}
                              </Typography>
                            </Alert>
                          )}

                        {/* File upload button - only show for new questionnaires or fields that need clarification */}
                        {(!questionnaireData?.is_clarification ||
                          clarificationFields.has(field.source)) && (
                          <Box sx={{ mb: 2 }}>
                            <Button
                              variant="outlined"
                              component="label"
                              startIcon={<UploadIcon />}
                              disabled={
                                submitting ||
                                !areRequiredFieldsFilled(field.source)
                              }
                            >
                              <Typography>
                                {!areRequiredFieldsFilled(field.source)
                                  ? 'Fill required fields in this section first'
                                  : 'Add Attachment'}
                              </Typography>
                              {!areRequiredFieldsFilled(field.source) && (
                                <FormHelperText sx={{ ml: 1 }}>
                                  Please fill in all required fields in this
                                  section before uploading attachments
                                </FormHelperText>
                              )}
                              {areRequiredFieldsFilled(field.source) && (
                                <FormHelperText sx={{ ml: 1 }}>
                                  Allowed file types: PDF, DOCX, XLSX, PNG, JPG
                                </FormHelperText>
                              )}
                              <input
                                type="file"
                                hidden
                                multiple
                                accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/png,image/jpeg"
                                onChange={async (e) => {
                                  const { files } = e.target
                                  if (!files || files.length === 0) return

                                  // Initialize attachments array if it doesn't exist
                                  if (!field.attachments) {
                                    field.attachments = []
                                  }

                                  // Process all files in parallel
                                  const uploadPromises = Array.from(files).map(
                                    async (file) => {
                                      try {
                                        // Create form data for the file upload
                                        const formData = new FormData()
                                        formData.append('document', file)
                                        formData.append(
                                          'field_id',
                                          field.id || ''
                                        )
                                        formData.append(
                                          'field_source',
                                          field.source || ''
                                        )

                                        // Upload the file
                                        const response = await fetch(
                                          `${API_URL}/questionnaire/${token}/attachments`,
                                          {
                                            method: 'POST',
                                            body: formData,
                                          }
                                        )

                                        if (!response.ok) {
                                          throw new Error(
                                            `Upload failed: ${response.statusText}`
                                          )
                                        }

                                        const data = await response.json()

                                        return {
                                          success: true,
                                          data: {
                                            id: data.id,
                                            filename: data.original_filename,
                                            url: `${API_URL}/questionnaire/${token}/attachments/${data.id}`,
                                          },
                                        }
                                      } catch (uploadError) {
                                        console.error(
                                          'File upload error:',
                                          uploadError
                                        )
                                        setError(
                                          uploadError instanceof Error
                                            ? uploadError.message
                                            : `Upload failed for ${file.name}`
                                        )
                                        return { success: false }
                                      }
                                    }
                                  )

                                  // Wait for all uploads to complete
                                  const results = await Promise.all(
                                    uploadPromises
                                  )

                                  // Process successful uploads
                                  results.forEach((result) => {
                                    if (result.success && result.data) {
                                      // Add the new attachment to the field
                                      if (!field.attachments) {
                                        field.attachments = []
                                      }
                                      field.attachments.push(result.data)

                                      // Mark the field as answered
                                      handleInputChange(
                                        field.source,
                                        'attachment_uploaded'
                                      )
                                    }
                                  })

                                  // Clear the file input
                                  e.target.value = ''
                                }}
                                disabled={submitting}
                              />
                            </Button>
                          </Box>
                        )}

                        {/* Display newly uploaded files */}
                        {!questionnaireData?.is_clarification &&
                        field.attachments &&
                        field.attachments.length > 0 ? (
                          <Box>
                            <Typography variant="subtitle2" gutterBottom>
                              Uploaded Files:
                            </Typography>
                            {field.attachments.map((attachment, index) => (
                              <Box key={attachment.id || index} sx={{ mb: 1 }}>
                                <Link
                                  href={attachment.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {attachment.filename}
                                </Link>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          !questionnaireData?.is_clarification && (
                            <Typography variant="body2" color="textSecondary">
                              No files uploaded yet
                            </Typography>
                          )
                        )}

                        {field.required &&
                          !answers[field.source] &&
                          error !== null && (
                            <FormHelperText error>
                              This field is required
                            </FormHelperText>
                          )}

                        {/* Add clarification response text field if needed */}
                        {questionnaireData?.is_clarification &&
                          clarificationFields.has(field.source) && (
                            <Box mt={2}>
                              <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Your Clarification Response"
                                name={`${field.source}_clarification`}
                                value={
                                  clarificationResponses[field.source] || ''
                                }
                                onChange={(e) =>
                                  handleClarificationResponseChange(
                                    field.source,
                                    e.target.value
                                  )
                                }
                                variant="outlined"
                                placeholder="Please provide additional information as requested..."
                              />
                            </Box>
                          )}
                      </Box>
                    ) : (
                      <Box>
                        <TextField
                          fullWidth
                          label={field.label}
                          name={field.source}
                          value={answers[field.source] || ''}
                          onChange={(e) =>
                            handleInputChange(field.source, e.target.value)
                          }
                          required={field.required}
                          disabled={questionnaireData?.is_clarification}
                          error={
                            field.required &&
                            !answers[field.source] &&
                            error !== null
                          }
                          helperText={
                            field.required &&
                            !answers[field.source] &&
                            error !== null
                              ? 'This field is required'
                              : questionnaireData?.is_clarification &&
                                !clarificationFields.has(field.source)
                              ? 'This field does not need clarification'
                              : ''
                          }
                          sx={{
                            backgroundColor:
                              questionnaireData?.is_clarification &&
                              clarificationFields.has(field.source)
                                ? 'rgba(255, 244, 229, 0.2)' // Light warning color background
                                : 'inherit',
                          }}
                        />

                        {/* Show clarification request if this field needs clarification */}
                        {questionnaireData?.is_clarification &&
                          clarificationFields.has(field.source) && (
                            <>
                              <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                                <AlertTitle>Clarification Requested</AlertTitle>
                                <Typography variant="body2" gutterBottom>
                                  {questionnaireData.clarification_requests?.[
                                    field.source
                                  ]?.comment ||
                                    'Please provide more information for this question.'}
                                </Typography>
                              </Alert>
                              <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Your Clarification Response"
                                name={`${field.source}_clarification`}
                                value={
                                  clarificationResponses[field.source] || ''
                                }
                                onChange={(e) =>
                                  handleClarificationResponseChange(
                                    field.source,
                                    e.target.value
                                  )
                                }
                                variant="outlined"
                                sx={{ mt: 1 }}
                                placeholder="Please provide additional information as requested..."
                              />
                            </>
                          )}
                      </Box>
                    )}

                    {/* Vendor Comment field */}
                    {field.allow_vendor_comment && (
                      <Box mt={2}>
                        <Typography variant="subtitle2" gutterBottom>
                          Vendor Comment
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          name={`${field.source}_vendor_comment`}
                          value={
                            answers[`${field.source}_vendor_comment`] || ''
                          }
                          onChange={(e) =>
                            handleInputChange(
                              `${field.source}_vendor_comment`,
                              e.target.value
                            )
                          }
                          variant="outlined"
                        />
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )
          )}

          {/* Additional Document Uploads section */}
          {questionnaireData?.allow_additional_document_uploads && (
            <Box mt={4} p={3} border={1} borderColor="divider" borderRadius={1}>
              <Typography
                variant="h6"
                gutterBottom
                display="flex"
                alignItems="center"
              >
                <UploadIcon sx={{ mr: 1 }} />
                Additional Document Uploads
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Upload any other relevant files not specifically requested in
                the questionnaire.
              </Typography>

              {/* File upload button */}
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                  disabled={submitting}
                >
                  <Typography>Add Additional Documents</Typography>
                  <FormHelperText sx={{ ml: 1 }}>
                    Allowed file types: PDF, DOCX, XLSX, PNG, JPG
                  </FormHelperText>
                  <input
                    type="file"
                    hidden
                    multiple
                    accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/png,image/jpeg"
                    onChange={async (e) => {
                      const { files } = e.target
                      if (!files || files.length === 0) return

                      // Process all files in parallel
                      const uploadPromises = Array.from(files).map(
                        async (file) => {
                          try {
                            // Create form data for the file upload
                            const formData = new FormData()
                            formData.append('document', file)
                            formData.append(
                              'field_id',
                              additionalAttachmentsFieldId
                            )
                            formData.append(
                              'field_source',
                              additionalAttachmentsFieldId
                            )

                            // Upload the file
                            const response = await fetch(
                              `${API_URL}/questionnaire/${token}/attachments`,
                              {
                                method: 'POST',
                                body: formData,
                              }
                            )

                            if (!response.ok) {
                              throw new Error(
                                `Upload failed: ${response.statusText}`
                              )
                            }

                            const data = await response.json()

                            return {
                              success: true,
                              data: {
                                id: data.id,
                                filename: data.original_filename,
                                url: `${API_URL}/questionnaire/${token}/attachments/${data.id}`,
                              },
                            }
                          } catch (uploadError) {
                            console.error('File upload error:', uploadError)
                            setError(
                              uploadError instanceof Error
                                ? uploadError.message
                                : `Upload failed for ${file.name}`
                            )
                            return { success: false }
                          }
                        }
                      )

                      // Wait for all uploads to complete
                      const results = await Promise.all(uploadPromises)

                      // Process successful uploads
                      results.forEach((result) => {
                        if (result.success && result.data) {
                          // Add the new attachment to the state
                          setAdditionalAttachments((prevAttachments) => [
                            ...prevAttachments,
                            result.data,
                          ])
                        }
                      })

                      // Clear the file input
                      e.target.value = ''
                    }}
                    disabled={submitting}
                  />
                </Button>
              </Box>

              {/* Display uploaded files */}
              {additionalAttachments.length > 0 ? (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Uploaded Additional Documents:
                  </Typography>
                  {additionalAttachments.map((attachment, index) => (
                    <Box
                      key={attachment.id || index}
                      sx={{
                        mb: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Link
                        href={attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {attachment.filename}
                      </Link>
                      <Button
                        size="small"
                        color="error"
                        onClick={async () => {
                          try {
                            // Delete the attachment
                            const response = await fetch(
                              `${API_URL}/questionnaire/${token}/attachments/${attachment.id}`,
                              {
                                method: 'DELETE',
                              }
                            )

                            if (!response.ok) {
                              throw new Error(
                                `Delete failed: ${response.statusText}`
                              )
                            }

                            // Remove the attachment from the state
                            setAdditionalAttachments((prevAttachments) =>
                              prevAttachments.filter(
                                (a) => a.id !== attachment.id
                              )
                            )
                          } catch (deleteError) {
                            console.error('File delete error:', deleteError)
                            setError(
                              deleteError instanceof Error
                                ? deleteError.message
                                : `Delete failed for ${attachment.filename}`
                            )
                          }
                        }}
                        disabled={submitting}
                      >
                        <DeleteIcon fontSize="small" />
                      </Button>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No additional documents uploaded yet
                </Typography>
              )}
            </Box>
          )}

          <Box mt={4} display="flex" justifyContent="flex-end">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting}
              size="large"
            >
              {submitting ? (
                <CircularProgress size={24} />
              ) : questionnaireData?.is_clarification ? (
                'Submit Clarifications'
              ) : (
                'Submit'
              )}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  )
}

export default PublicQuestionnaire
